import React from 'react';
import {
    Box,
    Cards,
    Link,
    SpaceBetween,
    TextContent,
} from '@cloudscape-design/components';
import { useTranslate } from 'src/i18n/useTranslate';
import { getLocaleDate } from 'src/helpers';
import { useNavGraph } from 'src/components/providers/NavGraphProvider';
import ArtifactCardHeader from 'src/components/ArtifactCardHeader/ArtifactCardHeader';

export interface ArtifactCard {
    id: number;
    breadcrumbPath?: string;
    title: string;
    locale?: string;
    description?: string;
    lastUpdatedDate?: string;
    language?: string;
    externalUrl?: string;
    topicId?: string;
    topicName?: string;
}

export interface ArtifactsSectionProps {
    artifacts: ArtifactCard[];
    name: string;
    viewAllPath: string;
}

export const ArtifactsSection = ({
    artifacts,
    name,
    viewAllPath,
}: ArtifactsSectionProps) => {
    const t = useTranslate();
    const { getBreadcrumbNamesFromPath } = useNavGraph();

    const buildBreadcrumbText = (path: string) => {
        const names = getBreadcrumbNamesFromPath(path);
        return names.join(' > ');
    };

    if (!artifacts.length) return null;

    return (
        <SpaceBetween size="m">
            <div className="flex-row justify-space-between align-items-center">
                <Box variant="h2" data-testid="artifacts-section-header">
                    {name}
                </Box>

                <Link href={viewAllPath}>
                    {t('landing_page_recently_view_all', 'View All')}
                </Link>
            </div>

            <Cards
                data-all-cards-container
                ariaLabels={{
                    itemSelectionLabel: (e, t) => `select ${t.title}`,
                    selectionGroupLabel: 'Item selection',
                }}
                cardsPerRow={[
                    { cards: 1 },
                    { minWidth: 750, cards: 2 },
                    { minWidth: 1000, cards: 4 },
                ]}
                cardDefinition={{
                    header: (item) => (
                        <ArtifactCardHeader
                            item={item}
                            testId={'artifacts-section-card'}
                        />
                    ),
                    sections: [
                        {
                            id: 'path',
                            header: '',
                            content: (item) => (
                                <TextContent>
                                    <p data-list-path>
                                        <small>
                                            {buildBreadcrumbText(
                                                item.breadcrumbPath || ''
                                            )}
                                        </small>
                                    </p>
                                </TextContent>
                            ),
                        },
                        {
                            id: 'last-modified',
                            header: '',
                            content: (item) => (
                                <TextContent>
                                    <p data-list-last-modified>
                                        <small>
                                            {t(
                                                'details-page-last-modified',
                                                'Last synced on'
                                            )}{' '}
                                            <small data-last-modified-date>
                                                {getLocaleDate(
                                                    item.lastUpdatedDate
                                                )}
                                            </small>
                                        </small>
                                    </p>
                                </TextContent>
                            ),
                        },
                    ],
                }}
                items={artifacts?.slice(0, 4)}
            />
        </SpaceBetween>
    );
};
